// 3D-viewer in de browser (module 3d). Gebundeld naar /js/3d/viewer.js.
//
// Zoekt elk element .viewer[data-glb] (gemaakt door viewerHtml op de server) en maakt er een
// three.js-viewer van:
// - draaien en zoomen (OrbitControls), onderdeel aanklikken in het model of in de lijst: de rij met
//   data-part-ids in de zaaglijst (data-zaaglijst = id van de tabel) krijgt tr.is-active;
// - bouwtekening per onderdeel (data-bouwtekening, JSON van de server): na een klik opent naast het
//   model een kaart met maten, de tekening en de knop "Laat zien hoe" (_bouwtekening.ts). De tekening
//   is dezelfde als in de bouw-PDF en komt uit de driehoeken van het aangeklikte onderdeel als die
//   kloppen met de zaaglijst (O-120b, core/tekening.ts). Een sleep of een knijpgebaar is geen klik;
// - schuif per bouwstap (data-stapschuif): stappen uit de bouwinstructie (data-stappen, JSON van de
//   server), anders de volgorde van het ontwerp. Bij openen staat het model meteen compleet, met de
//   stapschuif op de laatste stap. De knop "Bouwvolgorde" speelt van stap 1 tot het einde;
//   een tweede klik, de schuif, een klik in de onderdelenlijst
//   of de zaaglijst stopt het afspelen.
// - schroeven (O-134a, nodes met extras.soort "schroef", zie ../schroeven.ts): een schroef is zichtbaar zodra
//   alle onderdelen die hij verbindt er staan. Bij het afspelen vliegen de schroeven van een stap na het hout
//   langs hun as het hout in, punt eerst en kort na elkaar; met prefers-reduced-motion en bij de schuif staan
//   ze er meteen. Een klik op een schroef kiest niets en noemt alleen de maat. Ze tellen niet mee voor de
//   camera, de maatschets en de onderdelenlijst.
// - beslag (beslag P5, nodes met extras.soort "beslag", zie ../beslag.ts): verschijnt zodra alle onderdelen waar
//   het aan zit er staan, zonder vlucht. Een klik noemt alleen de naam. Telt niet mee voor de camera.
// - bewegingen (beslag P5, draaipunten met extras.soort "beweging"): het model staat in de stand uit het ontwerp.
//   Alleen met data-beweging (de knop "Open"/"Dicht", nu alleen in testmodus tot Stijn akkoord geeft) draait de
//   knop alle bewegingen tussen dicht en open. De bouwtekening rekent altijd met de dichte stand. Zonder
//   data-beweging gedraagt de viewer zich als vóór beslag P5: geen beslag, alles dicht, het oude camerakader.
// - draai-icoon bovenin het vak (data-rol="draai-hint"): verschijnt als het model er staat en
//   verdwijnt rustig zodra iemand het model voor het eerst draait of zoomt (O99);
// - AR op de telefoon via model-viewer (alleen geladen op een telefoon of tablet), met een
//   ondertekende GLB-link voor Scene Viewer en een USDZ als ios-src voor AR Quick Look;
// - "Maak afbeeldingen voor de PDF" (data-upload): een overzicht en een beeld per bouwstap van
//   1200 x 630 als JPEG, verstuurd met de header x-csrf-token;
// - vaste aanzichten voor andere browsercode (de miniaturen van de demo): zodra het model er staat,
//   krijgt het element .viewer de eigenschap viewer3d en de gebeurtenis "viewer:klaar", allebei met
//   { aanzicht(naam), aanzichtBeelden(namen, breedte, hoogte) } (ViewerApi).
//
// Geen inline scripts (CSP). Eén three voor viewer en model-viewer (esbuild-bundel).

import {
  Box3,
  Color,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  NeutralToneMapping,
  PerspectiveCamera,
  PMREMGenerator,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
  type Object3D,
  type Texture,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

import { BouwtekeningKaart, leesGegevens, type Geometrie, type OnderdeelTekening } from "./_bouwtekening.ts";
import { OVERGANG_MS, schroefVlucht, volgordeKnop, wachtBij } from "./_opbouw.ts";
import { schroefNode } from "./_schroeven.ts";
import { beslagNode, bewegingenVoorVlag, draaiDuur, staatOpen, zetHoek, type ViewerBeweging } from "./_beslag.ts";

interface Deel {
  id: string;
  label: string;
  mesh: Mesh;
  materiaal: MeshStandardMaterial;
  rand: LineBasicMaterial;
  basis: Color;
  /** Volgorde in het ontwerp (extras.step uit de GLB). */
  stapOntwerp: number;
  knop: HTMLButtonElement | null;
}

interface Schroef {
  /** Het object van de glTF-node: daarop staan de extras, de plek en de zichtbaarheid. */
  node: Object3D;
  /** De meshes van de schroef, voor het aanklikken. */
  meshes: Mesh[];
  /** "Schroef 4 × 50". */
  label: string;
  /** De onderdelen die de schroef verbindt. */
  onderdelen: string[];
  /** De plek in het hout en de richting het hout in (ruimte van de ouder-node). */
  eind: Vector3;
  richting: Vector3;
  /** Zo ver begint de vlucht voor de plek: de lengte plus een stukje, zodat de punt net buiten het hout begint. */
  afstand: number;
}

interface Beslag {
  node: Object3D;
  meshes: Mesh[];
  label: string;
  onderdelen: string[];
}

interface Stap {
  nr: number;
  tekst: string;
  onderdelen: string[];
}

interface ArElement extends HTMLElement {
  src: string | null;
  canActivateAR: boolean;
  activateAR(): Promise<void>;
}

const RANDKLEUR = new Color("#5a3a1b");
const KEUZEKLEUR = new Color("#b5652a");
const NIEUW_GLOED = new Color("#6b3a12");
const PAPIER = "#fbf7f1";
const BEELD_B = 1200;
const BEELD_H = 630;
const MAX_UPLOAD_TEKENS = 4_700_000;
const STANDAARD_STATUS = "Klik op een onderdeel om het in de zaaglijst te zien.";
const RAND_OPACITEIT = 0.55;
/** Zo ver buiten het hout begint de punt van een vliegende schroef (meters). */
const VLIEG_AANLOOP_M = 0.015;
/** Verder zoomt het beeld niet uit voor de bouwtekening; ligt de kaart over meer dan de helft, dan blijft er iets bedekt. */
const MIN_ZOOM_KAART = 0.45;

/** Kijkrichting per aanzicht (glTF: Y omhoog, +Z is de voorkant). "begin" is de beginstand. */
const AANZICHTEN = {
  begin: [0.55, 0.45, 0.7],
  iso: [0.7, 0.62, 0.7],
  voor: [0, 0.12, 1],
  zij: [1, 0.12, 0],
  boven: [0, 1, 0.3],
} as const satisfies Record<string, readonly [number, number, number]>;

export type Aanzicht = keyof typeof AANZICHTEN;

/** Wat andere browsercode met een viewer kan (element.viewer3d en de gebeurtenis "viewer:klaar"). */
export interface ViewerApi {
  /** Camera naar een vast aanzicht; "begin" is de beginstand. */
  aanzicht(naam: Aanzicht): void;
  /** Kleine PNG-beelden (data-URL, doorzichtige achtergrond) van de aanzichten, zonder de camera te verplaatsen. */
  aanzichtBeelden(namen: Aanzicht[], breedte: number, hoogte: number): Partial<Record<Aanzicht, string>>;
}

function richtingVan(naam: string): Vector3 {
  const r = AANZICHTEN[naam as Aanzicht] ?? AANZICHTEN.begin;
  return new Vector3(r[0], r[1], r[2]).normalize();
}

/** Maakt een element zichtbaar. .btn zet display en wint van [hidden], dus de server zet ook display:none. */
function toon(el: HTMLElement) {
  el.hidden = false;
  el.style.removeProperty("display");
}

function minderBeweging(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

function absoluut(pad: string): string {
  return new URL(pad, location.href).href;
}

function csrfToken(): string | null {
  const veld = document.querySelector<HTMLInputElement>('input[name="_csrf"]');
  return veld?.value || null;
}

function magAr(): boolean {
  const ua = navigator.userAgent;
  return /Android|iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function wacht(el: HTMLElement, gebeurtenis: string, ms: number): Promise<void> {
  return new Promise((klaar) => {
    const t = setTimeout(klaar, ms);
    el.addEventListener(gebeurtenis, () => {
      clearTimeout(t);
      klaar();
    }, { once: true });
  });
}

async function leesFout(res: Response, standaard: string): Promise<string> {
  try {
    const j = (await res.json()) as { fout?: { bericht?: string } };
    return j.fout?.bericht ?? standaard;
  } catch {
    return standaard;
  }
}

class Viewer {
  private readonly stage: HTMLElement;
  private readonly statusEl: HTMLElement | null;
  private readonly titel: string;
  private renderer!: WebGLRenderer;
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(35, 1, 0.01, 50);
  private controls!: OrbitControls;
  private readonly delen: Deel[] = [];
  private readonly schroeven: Schroef[] = [];
  private readonly beslag: Beslag[] = [];
  private bewegingen: ViewerBeweging[] = [];
  /** Lopende draai van de knop Open/Dicht. */
  private draai: { start: number } | null = null;
  private keuze = new Set<string>();
  private stappen: Stap[] = [];
  private stappenUitBouwinstructie = false;
  private stapIndex = 0;
  private readonly box = new Box3();
  private tekenGepland = false;
  private arVerlooptMs = 0;
  /** true zodra de gebruiker het model draait of zoomt; tot die tijd past de camera zich aan het vak aan. */
  private bewogen = false;
  /** Gegevens per part-id voor de bouwtekening, of null zonder data-bouwtekening. */
  private readonly tekening: Map<string, OnderdeelTekening> | null;
  private kaart: BouwtekeningKaart | null = null;
  private readonly standaardStatus: string;
  /** Afspelen van de opbouw (O77). */
  private speelt = false;
  private speelTimer = 0;
  /** De stappen (indexen) die dit afspelen langsgaat en de plek daarin (O99). */
  private volgorde: number[] = [];
  private positie = 0;
  private draaiHint: HTMLElement | null = null;
  /** true als het afspelen stilstaat omdat de viewer buiten beeld is of het tabblad verborgen. */
  private wachtOpBeeld = false;
  private inBeeld = false;
  private speelKnop: HTMLButtonElement | null = null;
  /** Onderdelen die nu invaden, met het tijdstip van de start. */
  private overgang: { delen: Deel[]; start: number } | null = null;
  /** Schroeven die nu invliegen (O-134a). */
  private vlucht: { schroeven: Schroef[]; start: number } | null = null;

  constructor(private readonly el: HTMLElement) {
    this.stage = el.querySelector<HTMLElement>(".viewer-stage")!;
    this.statusEl = el.querySelector<HTMLElement>('[data-rol="status"]');
    this.titel = el.dataset.titel ?? "model";
    this.tekening = leesGegevens(el);
    this.standaardStatus = this.tekening ? "Klik op een onderdeel voor de maten en de maatschets." : STANDAARD_STATUS;
  }

  status(tekst: string) {
    if (this.statusEl) this.statusEl.textContent = tekst;
  }

  async start() {
    const canvas = document.createElement("canvas");
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", `3D-model van ${this.titel}. Sleep om te draaien.`);
    try {
      this.renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
    } catch {
      this.status("Je browser kan dit 3D-model niet tonen. De onderdelen staan hiernaast.");
      return;
    }
    this.stage.prepend(canvas);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = NeutralToneMapping;
    const pmrem = new PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.addEventListener("change", () => this.teken());
    // Draaien of zoomen stopt het afspelen niet (O99); de camera past zich alleen niet meer aan het vak aan.
    this.controls.addEventListener("start", () => {
      this.bewogen = true;
      this.verbergDraaiHint();
    });
    new ResizeObserver(() => this.pasMaatAan()).observe(this.stage);
    canvas.addEventListener("webglcontextlost", () => this.status("Het 3D-beeld is gestopt. Laad de pagina opnieuw."));

    this.status("Model laden...");
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(this.el.dataset.glb!);
    } catch {
      this.status("Het 3D-model kon niet worden geladen. Laad de pagina opnieuw.");
      return;
    }
    this.voegModelToe(gltf.scene);
    this.richtCamera();
    this.koppelKnoppen();
    this.koppelZaaglijst();
    if (this.tekening) {
      this.kaart = new BouwtekeningKaart(this.el, {
        gesloten: () => this.kies([], "model"),
        laatZien: (nr) => this.laatZien(nr),
      });
    }
    canvas.addEventListener("pointerdown", (e) => this.pointerNeer(e));
    canvas.addEventListener("pointerup", (e) => this.pointerOp(e, canvas));
    canvas.addEventListener("pointercancel", (e) => this.pointerWeg(e));

    if (this.el.dataset.stapschuif) await this.zetSchuifKlaar();
    this.status(this.standaardStatus);
    this.pasMaatAan();
    if (this.el.dataset.arLink || this.el.dataset.arGlb) void this.zetArKlaar();
    this.meldKlaar();
    this.toonDraaiHint();
    this.volgZichtbaarheid();
    this.startAutoDraai();
  }

  // ------------------------------------------------------------ vanzelf draaien

  /** Voorbeeldpagina's (data-autodraai): het model draait langzaam rond tot de bezoeker het aanraakt. */
  private startAutoDraai() {
    if (!this.el.dataset.autodraai) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const controls = this.controls;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 2;
    let aan = true;
    let inBeeld = true;
    controls.addEventListener("start", () => {
      aan = false;
      controls.autoRotate = false;
    });
    new IntersectionObserver((items) => {
      inBeeld = items.some((i) => i.isIntersecting);
    }).observe(this.el);
    const stap = () => {
      if (!aan) return;
      if (inBeeld && !document.hidden) controls.update();
      requestAnimationFrame(stap);
    };
    requestAnimationFrame(stap);
  }

  // ------------------------------------------------------------ draai-icoon

  private toonDraaiHint() {
    const hint = this.el.querySelector<HTMLElement>('[data-rol="draai-hint"]');
    if (!hint || this.bewogen) return;
    this.draaiHint = hint;
    toon(hint);
  }

  /** Laat het icoon rustig verdwijnen (de CSS-overgang van .is-weg); daarna is het echt weg. */
  private verbergDraaiHint() {
    const hint = this.draaiHint;
    if (!hint) return;
    this.draaiHint = null;
    hint.classList.add("is-weg");
    hint.setAttribute("aria-hidden", "true");
    const weg = () => (hint.hidden = true);
    hint.addEventListener("transitionend", weg, { once: true });
    setTimeout(weg, 1000);
  }

  // ------------------------------------------------------------ opbouw afspelen

  /** Houdt bij of de viewer in beeld is en het tabblad zichtbaar; daarna gaat een wachtend afspelen verder. */
  private volgZichtbaarheid() {
    if (typeof IntersectionObserver === "undefined") this.inBeeld = true;
    else {
      new IntersectionObserver(
        (items) => {
          this.inBeeld = items.some((i) => i.isIntersecting);
          this.hervat();
        },
        { threshold: 0.3 },
      ).observe(this.stage);
    }
    document.addEventListener("visibilitychange", () => this.hervat());
  }

  private magSpelen(): boolean {
    return this.inBeeld && document.visibilityState === "visible";
  }

  /**
   * Speelt de stappen van `volgorde` af vanuit het beginaanzicht, standaard van stap 1 tot het einde.
   * Het afspelen eindigt op de laatste stap van de volgorde. De camera blijft daarna waar hij is,
   * zodat je tijdens het afspelen kunt draaien.
   */
  private speelAf(volgorde = volgordeKnop(this.stappen.length)) {
    if (this.stappen.length < 2 || volgorde.length < 2) return;
    this.stopAfspelen();
    this.bewogen = false;
    this.richtCamera(richtingVan("begin"));
    this.keuze.clear();
    this.kies([], "model");
    this.volgorde = volgorde;
    this.positie = 0;
    this.zetSchuif(volgorde[0]!);
    this.toonStap(volgorde[0]!);
    this.speelt = true;
    this.zetSpeelKnop();
    this.planVolgende();
  }

  private planVolgende() {
    clearTimeout(this.speelTimer);
    if (!this.speelt) return;
    if (!this.magSpelen()) {
      this.wachtOpBeeld = true;
      return;
    }
    this.wachtOpBeeld = false;
    const wacht = wachtBij(this.volgorde, this.positie, this.stappen.length);
    this.speelTimer = window.setTimeout(() => this.volgendeStap(), wacht);
  }

  private volgendeStap() {
    if (!this.speelt) return;
    if (!this.magSpelen()) {
      this.wachtOpBeeld = true;
      return;
    }
    const stap = this.volgorde[this.positie + 1];
    if (stap === undefined) {
      this.stopAfspelen();
      return;
    }
    this.positie++;
    this.zetSchuif(stap);
    // Een stap terug: de onderdelen gaan in één keer weg. Een stap verder: de nieuwe vaden in.
    this.toonStap(stap, true);
    if (this.positie < this.volgorde.length - 1) this.planVolgende();
    else this.stopAfspelen();
  }

  private hervat() {
    if (this.speelt && this.wachtOpBeeld && this.magSpelen()) this.planVolgende();
  }

  private stopAfspelen() {
    clearTimeout(this.speelTimer);
    this.wachtOpBeeld = false;
    if (!this.speelt) return;
    this.speelt = false;
    this.zetSpeelKnop();
  }

  private zetSpeelKnop() {
    const knop = this.speelKnop;
    if (!knop) return;
    knop.setAttribute("aria-pressed", this.speelt ? "true" : "false");
    const teken = knop.querySelector<HTMLElement>('[data-rol="speel-teken"]');
    if (teken) teken.textContent = this.speelt ? "■" : "▶";
    knop.title = this.speelt ? "Stop het afspelen" : "Speel de opbouw af van stap 1 tot het einde";
  }

  private zetSchuif(i: number) {
    const schuif = this.el.querySelector<HTMLInputElement>('[data-rol="stap"]');
    if (schuif) schuif.value = String(i + 1);
  }

  /** Laat nieuwe onderdelen kort invaden. */
  private vaadIn(delen: Deel[]) {
    this.rondHoutAf();
    // Een onderdeel dat al doorzichtig is (glas), blijft zoals het is.
    delen = delen.filter((d) => !d.materiaal.transparent);
    if (!delen.length || minderBeweging()) return;
    for (const d of delen) {
      d.materiaal.transparent = true;
      d.materiaal.opacity = 0;
      d.materiaal.needsUpdate = true;
      d.rand.opacity = 0;
    }
    const overgang = { delen, start: performance.now() };
    this.overgang = overgang;
    const stap = (nu: number) => {
      if (this.overgang !== overgang) return;
      const t = Math.min(1, (nu - overgang.start) / OVERGANG_MS);
      const f = 1 - (1 - t) * (1 - t);
      for (const d of delen) {
        d.materiaal.opacity = f;
        d.rand.opacity = RAND_OPACITEIT * f;
      }
      this.renderer.render(this.scene, this.camera);
      if (t < 1) requestAnimationFrame(stap);
      else this.rondHoutAf();
    };
    requestAnimationFrame(stap);
  }

  /**
   * Laat de schroeven van een stap invliegen (O-134a): elk begint met de punt net buiten het hout en schuift
   * langs zijn as naar binnen. Een schroef die nog moet beginnen, is onzichtbaar.
   */
  private vliegIn(schroeven: Schroef[]) {
    if (!schroeven.length || minderBeweging()) return;
    const { starts, duur } = schroefVlucht(schroeven.length, wachtBij(this.volgorde, this.positie, this.stappen.length));
    for (const s of schroeven) s.node.visible = false;
    const vlucht = { schroeven, start: performance.now() };
    this.vlucht = vlucht;
    const stap = (nu: number) => {
      if (this.vlucht !== vlucht) return;
      let klaar = true;
      schroeven.forEach((s, i) => {
        const t = Math.max(0, Math.min(1, (nu - vlucht.start - starts[i]!) / Math.max(1, duur)));
        if (t < 1) klaar = false;
        s.node.visible = nu - vlucht.start >= starts[i]!;
        const f = 1 - (1 - t) * (1 - t);
        s.node.position.copy(s.eind).addScaledVector(s.richting, -s.afstand * (1 - f));
      });
      this.renderer.render(this.scene, this.camera);
      if (!klaar) requestAnimationFrame(stap);
      else this.rondVluchtAf();
    };
    requestAnimationFrame(stap);
  }

  /** Zet onderdelen die nog invaden en schroeven die nog invliegen meteen neer. */
  private rondOvergangAf() {
    this.rondVluchtAf();
    this.rondHoutAf();
  }

  private rondVluchtAf() {
    const v = this.vlucht;
    if (!v) return;
    this.vlucht = null;
    const zichtbaar = this.zichtbaarBij(this.stapIndex);
    for (const s of v.schroeven) {
      s.node.position.copy(s.eind);
      s.node.visible = this.schroefZichtbaar(s, zichtbaar);
    }
    this.teken();
  }

  /** Zet onderdelen die nog invaden meteen helemaal neer. */
  private rondHoutAf() {
    const o = this.overgang;
    if (!o) return;
    this.overgang = null;
    for (const d of o.delen) {
      d.materiaal.opacity = 1;
      d.materiaal.transparent = false;
      d.materiaal.needsUpdate = true;
      d.rand.opacity = RAND_OPACITEIT;
    }
    this.teken();
  }

  // ------------------------------------------------------------ aanzichten (ViewerApi)

  private meldKlaar() {
    const api: ViewerApi = {
      aanzicht: (naam) => this.aanzicht(naam),
      aanzichtBeelden: (namen, b, h) => this.aanzichtBeelden(namen, b, h),
    };
    (this.el as HTMLElement & { viewer3d?: ViewerApi }).viewer3d = api;
    this.el.dispatchEvent(new CustomEvent<ViewerApi>("viewer:klaar", { detail: api }));
  }

  private aanzicht(naam: Aanzicht) {
    if (this.box.isEmpty()) return;
    this.stopAfspelen();
    this.bewogen = naam !== "begin";
    this.richtCamera(richtingVan(naam));
    this.teken();
  }

  private aanzichtBeelden(namen: Aanzicht[], b: number, h: number): Partial<Record<Aanzicht, string>> {
    const uit: Partial<Record<Aanzicht, string>> = {};
    if (this.box.isEmpty() || !this.renderer) return uit;
    const canvas = this.renderer.domElement;
    const oud = { pos: this.camera.position.clone(), doel: this.controls.target.clone(), aspect: this.camera.aspect, near: this.camera.near, far: this.camera.far, b: canvas.width, h: canvas.height };
    const ratio = this.renderer.getPixelRatio();
    // De verschuiving voor de bouwtekening hoort niet in de miniaturen.
    const view = this.camera.view?.enabled ? { ...this.camera.view } : null;
    const zoom = this.camera.zoom;
    this.camera.clearViewOffset();
    this.camera.zoom = 1;
    // De miniaturen tonen het hele model, ook als de opbouw nog afspeelt.
    this.rondOvergangAf();
    const zichtbaar = this.delen.map((d) => d.mesh.visible);
    const schroefZichtbaar = this.schroeven.map((s) => s.node.visible);
    const beslagZichtbaar = this.beslag.map((b) => b.node.visible);
    for (const d of this.delen) d.mesh.visible = true;
    for (const s of this.schroeven) s.node.visible = true;
    for (const b of this.beslag) b.node.visible = true;
    this.kleur(new Set(), false);
    try {
      this.renderer.setSize(b, h, false);
      for (const naam of namen) {
        this.richtCamera(richtingVan(naam), b / h, 1.02);
        this.renderer.render(this.scene, this.camera);
        uit[naam] = canvas.toDataURL("image/png");
      }
    } finally {
      this.renderer.setSize(oud.b / ratio, oud.h / ratio, false);
      this.delen.forEach((d, i) => (d.mesh.visible = zichtbaar[i]!));
      this.schroeven.forEach((s, i) => (s.node.visible = schroefZichtbaar[i]!));
      this.beslag.forEach((b, i) => (b.node.visible = beslagZichtbaar[i]!));
      this.kleur();
      this.camera.position.copy(oud.pos);
      this.camera.aspect = oud.aspect;
      this.camera.near = oud.near;
      this.camera.far = oud.far;
      if (view) this.camera.setViewOffset(view.fullWidth, view.fullHeight, view.offsetX, view.offsetY, view.width, view.height);
      this.camera.zoom = zoom;
      this.camera.updateProjectionMatrix();
      this.controls.target.copy(oud.doel);
      this.controls.update();
      this.teken();
    }
    return uit;
  }

  // ------------------------------------------------------------ model

  private voegModelToe(root: Object3D) {
    const knoppen = new Map<string, HTMLButtonElement>();
    for (const k of this.el.querySelectorAll<HTMLButtonElement>(".viewer-part[data-part-id]")) knoppen.set(k.dataset.partId!, k);
    root.traverse((obj) => {
      if (!(obj instanceof Mesh)) return;
      const schroef = schroefNode(obj);
      if (schroef) {
        this.voegSchroefToe(schroef, obj);
        return;
      }
      // Beslag is geen onderdeel. Zonder de vlag (buiten testmodus) blijft het onzichtbaar, zoals vóór beslag P5.
      const beslag = beslagNode(obj);
      if (beslag) {
        if (this.el.dataset.beweging) this.voegBeslagToe(beslag, obj);
        return;
      }
      let o: Object3D | null = obj;
      while (o && o.userData.partId === undefined) o = o.parent;
      const info = (o?.userData ?? {}) as { partId?: string; label?: string; step?: number };
      const id = String(info.partId ?? obj.name);
      const bron = obj.material as MeshStandardMaterial;
      const materiaal = bron.clone();
      obj.material = materiaal;
      const rand = new LineBasicMaterial({ color: RANDKLEUR, transparent: true, opacity: RAND_OPACITEIT });
      const randen = new LineSegments(new EdgesGeometry(obj.geometry, 20), rand);
      randen.raycast = () => {};
      obj.add(randen);
      const knop = knoppen.get(id) ?? null;
      const label = knop?.querySelector(".viewer-part-name")?.textContent ?? info.label ?? id;
      this.delen.push({ id, label, mesh: obj, materiaal, rand, basis: materiaal.color.clone(), stapOntwerp: Number(info.step ?? 0), knop });
    });
    this.scene.add(root);
    this.bewegingen = bewegingenVoorVlag(root, Boolean(this.el.dataset.beweging));
    // De camera richt op het hout; een schroef die iets uitsteekt verschuift het beeld niet.
    root.updateMatrixWorld(true);
    this.box.makeEmpty();
    for (const d of this.delen) this.box.expandByObject(d.mesh);
    // Met bewegingen past het beeld bij dicht en open samen (beslag P5): een open deur blijft helemaal in beeld en
    // de camera springt niet bij Open of Dicht.
    if (this.bewegingen.length) {
      const hoeken = this.bewegingen.map((b) => b.hoek);
      for (const stand of ["dicht", "open"] as const) {
        for (const b of this.bewegingen) zetHoek(b, stand === "open" ? b.maxDeg : 0);
        root.updateMatrixWorld(true);
        for (const d of this.delen) this.box.expandByObject(d.mesh);
      }
      this.bewegingen.forEach((b, i) => zetHoek(b, hoeken[i]!));
      root.updateMatrixWorld(true);
    }
    if (this.box.isEmpty()) this.box.setFromObject(root);
  }

  /**
   * Een schroef uit het GLB (O-134a): de kop op de plek van de node, de punt langs de lokale +Y. Extras, plek en
   * draaiing komen van het object van de node (schroefNode); een node met meer meshes telt één keer.
   */
  private voegSchroefToe(node: Object3D, mesh: Mesh) {
    const al = this.schroeven.find((s) => s.node === node);
    if (al) {
      al.meshes.push(mesh);
      return;
    }
    const info = node.userData as { label?: unknown; onderdelen?: unknown; lengte_mm?: unknown };
    const lengte = typeof info.lengte_mm === "number" ? info.lengte_mm / 1000 : 0.04;
    this.schroeven.push({
      node,
      meshes: [mesh],
      label: typeof info.label === "string" ? info.label : "Schroef",
      onderdelen: Array.isArray(info.onderdelen) ? info.onderdelen.map(String) : [],
      eind: node.position.clone(),
      richting: new Vector3(0, 1, 0).applyQuaternion(node.quaternion).normalize(),
      afstand: lengte + VLIEG_AANLOOP_M,
    });
  }

  /** Beslag uit het GLB (beslag P5); een node met meer meshes telt één keer. */
  private voegBeslagToe(node: Object3D, mesh: Mesh) {
    const al = this.beslag.find((b) => b.node === node);
    if (al) {
      al.meshes.push(mesh);
      return;
    }
    const info = node.userData as { label?: unknown; onderdelen?: unknown };
    this.beslag.push({
      node,
      meshes: [mesh],
      label: typeof info.label === "string" && info.label ? info.label : "Beslag",
      onderdelen: Array.isArray(info.onderdelen) ? info.onderdelen.map(String) : [],
    });
  }

  /** Zet de zichtbaarheid van schroeven en beslag bij de zichtbare onderdelen. */
  private zetBevestigers(zichtbaar: Set<string>) {
    for (const s of this.schroeven) s.node.visible = this.schroefZichtbaar(s, zichtbaar);
    for (const b of this.beslag) b.node.visible = this.schroefZichtbaar(b, zichtbaar);
  }

  /** Een schroef staat er zodra alle onderdelen die hij verbindt (en die in het model staan) zichtbaar zijn. */
  private schroefZichtbaar(s: Pick<Schroef, "onderdelen">, zichtbaar: Set<string>): boolean {
    return s.onderdelen.every((id) => zichtbaar.has(id) || !this.delen.some((d) => d.id === id));
  }

  private richtCamera(richting = richtingVan("begin"), vakAspect?: number, vasteMarge?: number) {
    const maat = this.box.getSize(new Vector3());
    const midden = this.box.getCenter(new Vector3());
    // Past de bol om het model in de smalste kijkhoek (op een telefoon is het vak smaller dan hoog).
    const aspect = vakAspect ?? (this.stage.clientWidth && this.stage.clientHeight ? this.stage.clientWidth / this.stage.clientHeight : 1.6);
    const vfov = (this.camera.fov * Math.PI) / 180;
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * aspect);
    // In een smal vak bepaalt de breedte van het model de maat; houd dan wat ruimte voor de knoppen.
    const marge = vasteMarge ?? (aspect < 1.3 ? 1.12 : 0.98);
    const afstand = (maat.length() / 2 / Math.sin(Math.min(vfov, hfov) / 2)) * marge;
    if (vakAspect !== undefined) this.camera.aspect = vakAspect;
    this.camera.position.copy(midden).addScaledVector(richting, afstand);
    this.camera.near = afstand / 100;
    this.camera.far = afstand * 20;
    this.camera.updateProjectionMatrix();
    this.controls.target.copy(midden);
    this.controls.update();
  }

  private pasMaatAan() {
    if (!this.renderer) return;
    const b = this.stage.clientWidth;
    const h = this.stage.clientHeight;
    if (!b || !h) return;
    this.renderer.setSize(b, h, false);
    this.camera.aspect = b / h;
    this.camera.updateProjectionMatrix();
    if (!this.bewogen && !this.box.isEmpty()) this.richtCamera();
    if (this.kaart) {
      this.kaart.plaats();
      this.maakRuimteVoorKaart();
    }
    this.teken();
  }

  private teken() {
    if (this.tekenGepland) return;
    this.tekenGepland = true;
    requestAnimationFrame(() => {
      this.tekenGepland = false;
      this.renderer.render(this.scene, this.camera);
    });
  }

  // ------------------------------------------------------------ kiezen

  /** Beginpunt per vinger of muis. Een klik is één pointer die hooguit 5 px beweegt. */
  private readonly neer = new Map<number, [number, number]>();
  /** true zodra er in dit gebaar twee pointers tegelijk waren (knijpen om te zoomen). */
  private meerVingers = false;

  private pointerNeer(e: PointerEvent) {
    this.neer.set(e.pointerId, [e.clientX, e.clientY]);
    if (this.neer.size > 1) this.meerVingers = true;
  }

  private pointerWeg(e: PointerEvent) {
    this.neer.delete(e.pointerId);
    if (this.neer.size === 0) this.meerVingers = false;
  }

  private pointerOp(e: PointerEvent, canvas: HTMLCanvasElement) {
    const start = this.neer.get(e.pointerId);
    const knijp = this.meerVingers;
    this.pointerWeg(e);
    if (!start || knijp || Math.hypot(e.clientX - start[0], e.clientY - start[1]) > 5) return;
    const r = canvas.getBoundingClientRect();
    const ndc = new Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const straal = new Raycaster();
    straal.setFromCamera(ndc, this.camera);
    const meshes = [
      ...this.delen.filter((d) => d.mesh.visible).map((d) => d.mesh),
      ...this.schroeven.filter((s) => s.node.visible).flatMap((s) => s.meshes),
      ...this.beslag.filter((b) => b.node.visible).flatMap((b) => b.meshes),
    ];
    const raak = straal.intersectObjects(meshes, false)[0];
    // Een schroef kiest niets: alleen de maat in de statusregel.
    const schroef = raak ? this.schroeven.find((s) => s.meshes.includes(raak.object as Mesh)) : undefined;
    if (schroef) {
      this.status(`${schroef.label}.`);
      return;
    }
    const beslag = raak ? this.beslag.find((b) => b.meshes.includes(raak.object as Mesh)) : undefined;
    if (beslag) {
      this.status(`${beslag.label}.`);
      return;
    }
    const deel = raak ? this.delen.find((d) => d.mesh === raak.object) : undefined;
    this.kies(deel ? [deel.id] : [], "model");
  }

  kies(ids: string[], bron: "model" | "lijst" | "zaaglijst") {
    const zelfde = ids.length > 0 && ids.length === this.keuze.size && ids.every((i) => this.keuze.has(i));
    this.keuze = new Set(zelfde ? [] : ids);
    this.kleur();

    for (const d of this.delen) d.knop?.setAttribute("aria-pressed", this.keuze.has(d.id) ? "true" : "false");
    const eerste = this.delen.find((d) => this.keuze.has(d.id))?.knop;
    if (eerste && bron !== "lijst") this.scrolBinnen(eerste);

    let rijen = 0;
    const tabel = this.zaaglijst();
    if (tabel) {
      for (const rij of tabel.querySelectorAll<HTMLElement>("tr[data-part-ids]")) {
        const actief = (rij.dataset.partIds ?? "").split(/\s+/).some((id) => this.keuze.has(id));
        rij.classList.toggle("is-active", actief);
        if (actief) rijen++;
      }
    }

    const tekening = this.tekeningVoorKeuze();
    if (tekening) this.kaart?.toon(tekening, this.geometrieVoorKeuze());
    else this.kaart?.verberg();
    this.maakRuimteVoorKaart();

    if (this.keuze.size === 0) this.status(this.standaardStatus);
    else {
      // Een onderdeel kan uit meer meshes bestaan (bijvoorbeeld een gegraveerd vlak): elke naam één keer.
      const namen = [...new Set(this.delen.filter((d) => this.keuze.has(d.id)).map((d) => d.label))];
      const extra = !tabel ? "" : rijen ? " De rij in de zaaglijst is opgelicht." : " Dit onderdeel staat niet in de zaaglijst.";
      const kaart = tekening ? " De bouwtekening is open." : "";
      this.status(`Gekozen: ${namen.join(", ")}.${extra}${kaart}`);
    }
    this.teken();
  }

  /**
   * Ligt de kaart over het model (breed scherm), dan schuift het beeld de helft van die overlap naar
   * links, naar het midden van het vrije deel, en zoomt het uit met de breedte die vrij blijft (niet
   * verder dan MIN_ZOOM_KAART). Zo past alles wat zonder kaart in beeld was naast de kaart en blijft
   * de rechterkant van het model zichtbaar (O71). Dicht of onder het model: geen verschuiving, zoom 1.
   */
  private maakRuimteVoorKaart() {
    const b = this.stage.clientWidth;
    const h = this.stage.clientHeight;
    const overlap = this.kaart?.open ? this.kaart.overlapMet(this.stage) : 0;
    if (overlap > 0 && b && h) {
      this.camera.setViewOffset(b, h, overlap / 2, 0, b, h);
      this.camera.zoom = Math.min(1, Math.max(MIN_ZOOM_KAART, (b - overlap) / b));
    } else {
      this.camera.clearViewOffset();
      this.camera.zoom = 1;
    }
    this.camera.updateProjectionMatrix();
    this.teken();
  }

  /** De bouwtekening bij de keuze: één onderdeel, of meer onderdelen uit dezelfde zaaglijstregel. */
  private tekeningVoorKeuze(): OnderdeelTekening | null {
    if (!this.tekening || this.keuze.size === 0) return null;
    // Twee poten uit één regel hebben gelijke gegevens (na JSON wel twee objecten): vergelijk de inhoud.
    const gevonden = new Map<string, OnderdeelTekening | undefined>();
    for (const id of this.keuze) {
      const t = this.tekening.get(id);
      gevonden.set(t ? JSON.stringify(t) : `geen:${id}`, t);
    }
    return gevonden.size === 1 ? ([...gevonden.values()][0] ?? null) : null;
  }

  /**
   * De driehoeken van het eerste gekozen onderdeel in mm (wereldassen), voor de schets uit het
   * 3D-model (O95). Alle meshes met dat part-id tellen mee (een gegraveerd vlak is een eigen mesh).
   * Twee onderdelen uit één zaaglijstregel hebben dezelfde vorm, dus het eerste is genoeg.
   */
  private geometrieVoorKeuze(): Geometrie | null {
    const id = this.delen.find((d) => this.keuze.has(d.id))?.id;
    if (id === undefined) return null;
    const posities: number[] = [];
    const indices: number[] = [];
    const v = new Vector3();
    // De maatschets rekent met de dichte stand, zoals de zaaglijst: een open deur staat even dicht.
    const hoeken = this.bewegingen.map((b) => b.hoek);
    for (const b of this.bewegingen) zetHoek(b, 0);
    if (this.bewegingen.length) this.scene.updateMatrixWorld(true);
    for (const d of this.delen) {
      if (d.id !== id) continue;
      const pos = d.mesh.geometry.getAttribute("position");
      if (!pos) continue;
      d.mesh.updateWorldMatrix(true, false);
      const basis = posities.length / 3;
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(d.mesh.matrixWorld);
        posities.push(v.x * 1000, v.y * 1000, v.z * 1000);
      }
      const idx = d.mesh.geometry.getIndex();
      if (idx) for (let i = 0; i < idx.count; i++) indices.push(basis + idx.getX(i));
      else for (let i = 0; i < pos.count; i++) indices.push(basis + i);
    }
    this.bewegingen.forEach((b, i) => zetHoek(b, hoeken[i]!));
    if (this.bewegingen.length) this.scene.updateMatrixWorld(true);
    return indices.length ? { posities, indices } : null;
  }

  /** "Laat zien hoe": de schuif naar die bouwstap en scrollen naar de stap in de lijst op de pagina. */
  private laatZien(nr: number) {
    this.stopAfspelen();
    const i = this.stappenUitBouwinstructie ? this.stappen.findIndex((s) => s.nr === nr) : -1;
    const schuif = this.el.querySelector<HTMLInputElement>('[data-rol="stap"]');
    if (i >= 0 && schuif && this.stappen.length > 1) {
      schuif.value = String(i + 1);
      this.toonStap(i);
    }
    const doel = document.getElementById(`bouwstap-${nr}`);
    if (!doel) {
      this.el.scrollIntoView({ block: "start" });
      return;
    }
    for (let o: HTMLElement | null = doel.parentElement; o; o = o.parentElement) if (o instanceof HTMLDetailsElement) o.open = true;
    doel.tabIndex = -1;
    doel.scrollIntoView({ block: "center" });
    doel.focus({ preventScroll: true });
  }

  private scrolBinnen(el: HTMLElement) {
    const lijst = el.closest<HTMLElement>(".viewer-list");
    if (!lijst) return;
    const boven = el.offsetTop - lijst.offsetTop;
    if (boven < lijst.scrollTop) lijst.scrollTop = boven;
    else if (boven + el.offsetHeight > lijst.scrollTop + lijst.clientHeight) lijst.scrollTop = boven + el.offsetHeight - lijst.clientHeight;
  }

  private zaaglijst(): HTMLElement | null {
    const id = this.el.dataset.zaaglijst;
    return id ? document.getElementById(id) : null;
  }

  private kleur(nieuw: Set<string> = this.nieuweDelen(), metKeuze = true) {
    for (const d of this.delen) {
      if (metKeuze && this.keuze.has(d.id)) {
        d.materiaal.color.copy(KEUZEKLEUR);
        d.materiaal.emissive.setRGB(0.1, 0.04, 0);
      } else {
        d.materiaal.color.copy(d.basis);
        if (nieuw.has(d.id)) d.materiaal.emissive.copy(NIEUW_GLOED).multiplyScalar(0.35);
        else d.materiaal.emissive.setRGB(0, 0, 0);
      }
    }
  }

  // ------------------------------------------------------------ bouwstappen

  private async laadStappen(): Promise<Stap[]> {
    const pad = this.el.dataset.stappen;
    const ids = new Set(this.delen.map((d) => d.id));
    if (pad) {
      try {
        const res = await fetch(pad, { headers: { accept: "application/json" } });
        if (res.ok) {
          const j = (await res.json()) as { stappen?: Array<{ nr: number; tekst: string; onderdelen: string[] }> };
          const stappen = (j.stappen ?? [])
            .filter((s) => Number.isInteger(s.nr) && s.nr >= 1)
            .map((s) => ({ nr: s.nr, tekst: String(s.tekst ?? ""), onderdelen: (s.onderdelen ?? []).filter((o) => ids.has(o)) }));
          if (stappen.length) {
            this.stappenUitBouwinstructie = true;
            return stappen;
          }
        }
      } catch {
        // Geen bouwstappen: dan de volgorde van het ontwerp.
      }
    }
    this.stappenUitBouwinstructie = false;
    const delen = [...new Map(this.delen.map((d) => [d.id, d])).values()].sort(
      (a, b) => a.stapOntwerp - b.stapOntwerp || a.label.localeCompare(b.label, "nl"),
    );
    return delen.map((d, i) => ({ nr: i + 1, tekst: d.label, onderdelen: [d.id] }));
  }

  private async zetSchuifKlaar() {
    this.stappen = await this.laadStappen();
    const label = this.el.querySelector<HTMLLabelElement>('[data-rol="schuif"] label');
    if (label) label.textContent = this.stappenUitBouwinstructie ? "Bouwstap" : "Opbouw";
    const blok = this.el.querySelector<HTMLElement>('[data-rol="schuif"]');
    const schuif = this.el.querySelector<HTMLInputElement>('[data-rol="stap"]');
    if (!blok || !schuif || this.stappen.length < 2) {
      this.stapIndex = Math.max(0, this.stappen.length - 1);
      return;
    }
    // Stapnummer in de onderdelenlijst: de eerste bouwstap waarin het onderdeel voorkomt.
    if (this.stappenUitBouwinstructie) {
      for (const d of this.delen) {
        const i = this.stappen.findIndex((s) => s.onderdelen.includes(d.id));
        const dim = d.knop?.querySelector(".viewer-part-dim");
        if (dim) dim.textContent = i >= 0 ? `stap ${this.stappen[i]!.nr}` : "";
      }
    }
    schuif.min = "1";
    schuif.max = String(this.stappen.length);
    schuif.value = String(this.stappen.length);
    toon(blok);
    schuif.addEventListener("input", () => {
      this.stopAfspelen();
      this.toonStap(Number(schuif.value) - 1);
    });
    this.toonStap(this.stappen.length - 1);
    const knop = this.el.querySelector<HTMLButtonElement>('[data-actie="bouwvolgorde"]');
    if (knop) {
      this.speelKnop = knop;
      this.zetSpeelKnop();
      toon(knop);
      knop.addEventListener("click", () => (this.speelt ? this.stopAfspelen() : this.speelAf()));
    }
  }

  /** Welke onderdelen bij stap i zichtbaar zijn. De laatste stap toont alles. */
  private zichtbaarBij(i: number): Set<string> {
    const alle = new Set(this.delen.map((d) => d.id));
    if (!this.stappen.length || i >= this.stappen.length - 1) return alle;
    const genoemd = new Set(this.stappen.flatMap((s) => s.onderdelen));
    const zichtbaar = new Set([...alle].filter((id) => !genoemd.has(id)));
    let iets = false;
    for (const s of this.stappen.slice(0, i + 1)) {
      for (const id of s.onderdelen) {
        zichtbaar.add(id);
        iets = true;
      }
    }
    return iets || zichtbaar.size ? zichtbaar : alle;
  }

  private nieuweDelen(i: number = this.stapIndex): Set<string> {
    if (!this.stappen.length || this.stappen.length < 2) return new Set();
    return new Set(this.stappen[i]?.onderdelen ?? []);
  }

  /** Toont stap i; met overgang vaden de onderdelen die er nieuw bij komen kort in en vliegen daarna hun schroeven in. */
  toonStap(i: number, overgang = false) {
    this.rondVluchtAf();
    this.stapIndex = Math.max(0, Math.min(i, this.stappen.length - 1));
    const zichtbaar = this.zichtbaarBij(this.stapIndex);
    const nieuw = this.delen.filter((d) => !d.mesh.visible && zichtbaar.has(d.id));
    for (const d of this.delen) d.mesh.visible = zichtbaar.has(d.id);
    const nieuweSchroeven = this.schroeven.filter((s) => !s.node.visible && this.schroefZichtbaar(s, zichtbaar));
    this.zetBevestigers(zichtbaar);
    this.vaadIn(overgang ? nieuw : []);
    if (overgang) this.vliegIn(nieuweSchroeven);
    const stap = this.stappen[this.stapIndex];
    const uit = this.el.querySelector<HTMLOutputElement>('[data-rol="stap-uit"]');
    if (uit && stap) {
      const tekst = stap.tekst.length > 90 ? `${stap.tekst.slice(0, 87)}...` : stap.tekst;
      uit.textContent = `${this.stappenUitBouwinstructie ? "Stap" : "Onderdeel"} ${stap.nr} van ${this.stappen.length}${tekst ? `: ${tekst}` : ""}`;
    }
    this.kleur();
    this.teken();
  }

  // ------------------------------------------------------------ knoppen en zaaglijst

  private koppelKnoppen() {
    // Eén listener per knop: een onderdeel met meer meshes staat vaker in delen, en twee keer kies()
    // met hetzelfde onderdeel zet de keuze weer uit.
    const gekoppeld = new Set<HTMLButtonElement>();
    for (const d of this.delen) {
      if (!d.knop || gekoppeld.has(d.knop)) continue;
      gekoppeld.add(d.knop);
      const id = d.id;
      d.knop.addEventListener("click", () => {
        this.stopAfspelen();
        this.kies([id], "lijst");
      });
    }
    this.koppelBeweging();
    // De knop "Bouwvolgorde" koppelt zetSchuifKlaar, want die hangt af van de stappen.
    const beelden = this.el.querySelector<HTMLButtonElement>('[data-actie="stapbeelden"]');
    if (beelden && this.el.dataset.upload) {
      toon(beelden);
      beelden.addEventListener("click", () => {
        this.stopAfspelen();
        void this.stapbeeldenKnop(beelden);
      });
    }
  }

  // ------------------------------------------------------------ open en dicht (beslag P5)

  /** De knop Open/Dicht: alleen met data-beweging (testmodus) en als het model bewegingen heeft. */
  private koppelBeweging() {
    const knop = this.el.querySelector<HTMLButtonElement>('[data-actie="beweging"]');
    if (!knop || !this.el.dataset.beweging || !this.bewegingen.length) return;
    this.zetBewegingKnop(knop);
    toon(knop);
    knop.addEventListener("click", () => {
      this.stopAfspelen();
      this.draaiNaar(!staatOpen(this.bewegingen), knop);
    });
  }

  private zetBewegingKnop(knop: HTMLButtonElement, open = staatOpen(this.bewegingen)) {
    knop.setAttribute("aria-pressed", open ? "true" : "false");
    const tekst = knop.querySelector<HTMLElement>('[data-rol="beweging-tekst"]');
    if (tekst) tekst.textContent = open ? "Dicht" : "Open";
    knop.title = open ? "Doe het model dicht" : "Doe het model open";
  }

  /** Draait elke beweging naar open (maxDeg) of dicht (0). Met prefers-reduced-motion staat hij er meteen. */
  private draaiNaar(open: boolean, knop: HTMLButtonElement) {
    const van = this.bewegingen.map((b) => b.hoek);
    const naar = this.bewegingen.map((b) => (open ? b.maxDeg : 0));
    this.zetBewegingKnop(knop, open);
    const duur = Math.max(...this.bewegingen.map((b, i) => draaiDuur(van[i]!, naar[i]!, b.kind)));
    if (minderBeweging()) {
      this.draai = null;
      this.bewegingen.forEach((b, i) => zetHoek(b, naar[i]!));
      this.teken();
      return;
    }
    const draai = { start: performance.now() };
    this.draai = draai;
    const stap = (nu: number) => {
      if (this.draai !== draai) return;
      const t = Math.min(1, (nu - draai.start) / duur);
      const f = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      this.bewegingen.forEach((b, i) => zetHoek(b, van[i]! + (naar[i]! - van[i]!) * f));
      this.renderer.render(this.scene, this.camera);
      if (t < 1) requestAnimationFrame(stap);
      else this.draai = null;
    };
    requestAnimationFrame(stap);
  }

  private koppelZaaglijst() {
    const tabel = this.zaaglijst();
    if (!tabel) return;
    tabel.addEventListener("click", (e) => {
      const rij = (e.target as HTMLElement).closest<HTMLElement>("tr[data-part-ids]");
      if (!rij) return;
      const ids = (rij.dataset.partIds ?? "").split(/\s+/).filter((id) => this.delen.some((d) => d.id === id));
      if (!ids.length) return;
      this.stopAfspelen();
      this.kies(ids, "zaaglijst");
    });
  }

  // ------------------------------------------------------------ AR

  private async zetArKlaar() {
    if (!magAr()) return;
    const knop = this.el.querySelector<HTMLButtonElement>('[data-actie="ar"]');
    if (!knop) return;
    try {
      await import("@google/model-viewer");
    } catch {
      return;
    }
    const mv = document.createElement("model-viewer") as ArElement;
    mv.setAttribute("ar", "");
    mv.setAttribute("ar-modes", "webxr scene-viewer quick-look");
    mv.setAttribute("ar-scale", "fixed");
    mv.setAttribute("loading", "eager");
    mv.setAttribute("alt", `3D-model van ${this.titel}`);
    mv.setAttribute("aria-hidden", "true");
    // In .viewer-stage vult model-viewer normaal het vak; hier is hij alleen de AR-motor.
    mv.style.cssText = "inset:auto;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none";
    mv.addEventListener("load", () => {
      if (mv.canActivateAR) toon(knop);
    });
    this.stage.append(mv);
    try {
      await this.zetArBron(mv);
    } catch {
      return;
    }
    knop.addEventListener("click", async () => {
      try {
        if (Date.now() > this.arVerlooptMs) {
          await this.zetArBron(mv);
          await wacht(mv, "load", 5000);
        }
        await mv.activateAR();
      } catch {
        this.status("AR starten lukte niet op dit apparaat.");
      }
    });
  }

  private async zetArBron(mv: ArElement) {
    const link = this.el.dataset.arLink;
    if (link) {
      const res = await fetch(link, { headers: { accept: "application/json" } });
      if (!res.ok) throw new Error(await leesFout(res, "Geen AR-link."));
      const j = (await res.json()) as { glb: string; usdz: string; geldig_sec: number };
      mv.setAttribute("ios-src", absoluut(j.usdz));
      mv.setAttribute("src", absoluut(j.glb));
      this.arVerlooptMs = Date.now() + Math.max(60, j.geldig_sec - 60) * 1000;
    } else {
      if (this.el.dataset.arUsdz) mv.setAttribute("ios-src", absoluut(this.el.dataset.arUsdz));
      mv.setAttribute("src", absoluut(this.el.dataset.arGlb!));
      this.arVerlooptMs = Number.POSITIVE_INFINITY;
    }
  }

  // ------------------------------------------------------------ stapbeelden voor de PDF

  private async stapbeeldenKnop(knop: HTMLButtonElement) {
    const csrf = csrfToken();
    if (!csrf) {
      this.status("Log opnieuw in om afbeeldingen op te slaan.");
      return;
    }
    knop.disabled = true;
    this.status("Afbeeldingen maken...");
    try {
      const beelden = this.maakStapbeelden(0.85);
      let totaal = Object.values(beelden).reduce((s, b) => s + b.length, 0);
      let teVersturen = beelden;
      if (totaal > MAX_UPLOAD_TEKENS) {
        teVersturen = this.maakStapbeelden(0.65);
        totaal = Object.values(teVersturen).reduce((s, b) => s + b.length, 0);
        if (totaal > MAX_UPLOAD_TEKENS) throw new Error("Te veel bouwstappen voor één keer. Neem contact op als je dit nodig hebt.");
      }
      this.status("Afbeeldingen opslaan...");
      const res = await fetch(this.el.dataset.upload!, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json", "x-csrf-token": csrf },
        body: JSON.stringify({ beelden: teVersturen }),
      });
      if (!res.ok) throw new Error(await leesFout(res, "Opslaan lukte niet."));
      const j = (await res.json()) as { opgeslagen: string[] };
      const stappen = j.opgeslagen.filter((n) => n.startsWith("stap-")).length;
      this.status(
        stappen
          ? `${j.opgeslagen.length} afbeeldingen opgeslagen: een overzicht en ${stappen} bouwstap${stappen === 1 ? "" : "pen"}. De PDF gebruikt ze.`
          : "Het overzicht is opgeslagen. Er zijn nog geen bouwstappen, dus alleen het overzicht komt in de PDF.",
      );
    } catch (fout) {
      this.status(fout instanceof Error ? fout.message : "Er ging iets mis bij het maken van de afbeeldingen.");
    } finally {
      knop.disabled = false;
    }
  }

  /** Rendert het overzicht en elke bouwstap (uit de bouwinstructie) naar JPEG-data-URL's van 1200 x 630. */
  maakStapbeelden(kwaliteit: number): Record<string, string> {
    this.rondOvergangAf();
    const canvas = document.createElement("canvas");
    canvas.width = BEELD_B;
    canvas.height = BEELD_H;
    const r = new WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
    const pmrem = new PMREMGenerator(r);
    const env: Texture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    const oudeEnv = this.scene.environment;
    const oudeZichtbaar = this.delen.map((d) => d.mesh.visible);
    const oudeSchroeven = this.schroeven.map((s) => s.node.visible);
    const oudBeslag = this.beslag.map((b) => b.node.visible);
    try {
      r.setPixelRatio(1);
      r.setSize(BEELD_B, BEELD_H, false);
      r.toneMapping = NeutralToneMapping;
      r.setClearColor(PAPIER, 1);
      this.scene.environment = env;

      const cam = new PerspectiveCamera(30, BEELD_B / BEELD_H, 0.01, 50);
      const midden = this.box.getCenter(new Vector3());
      const straal = this.box.getSize(new Vector3()).length() / 2;
      const afstand = (straal / Math.sin(((cam.fov / 2) * Math.PI) / 180)) * 1.05;
      const richting = new Vector3(0.55, 0.45, 0.7).normalize();
      cam.position.copy(midden).addScaledVector(richting, afstand);
      cam.near = afstand / 100;
      cam.far = afstand * 10;
      cam.lookAt(midden);
      cam.updateProjectionMatrix();

      const beelden: Record<string, string> = {};
      const foto = (zichtbaar: Set<string>, nieuw: Set<string>) => {
        for (const d of this.delen) d.mesh.visible = zichtbaar.has(d.id);
        this.zetBevestigers(zichtbaar);
        this.kleur(nieuw, false);
        r.render(this.scene, cam);
        return canvas.toDataURL("image/jpeg", kwaliteit);
      };
      beelden.overzicht = foto(new Set(this.delen.map((d) => d.id)), new Set());
      if (this.stappenUitBouwinstructie) {
        this.stappen.forEach((s, i) => {
          if (s.nr > 99) return;
          beelden[`stap-${s.nr}`] = foto(this.zichtbaarBij(i), new Set(s.onderdelen));
        });
      }
      return beelden;
    } finally {
      this.scene.environment = oudeEnv;
      this.delen.forEach((d, i) => (d.mesh.visible = oudeZichtbaar[i]!));
      this.schroeven.forEach((s, i) => (s.node.visible = oudeSchroeven[i]!));
      this.beslag.forEach((b, i) => (b.node.visible = oudBeslag[i]!));
      this.kleur();
      env.dispose();
      pmrem.dispose();
      r.dispose();
      r.forceContextLoss();
      this.teken();
    }
  }
}

const viewers: Viewer[] = [];
for (const el of document.querySelectorAll<HTMLElement>(".viewer[data-glb]")) {
  const v = new Viewer(el);
  viewers.push(v);
  void v.start();
}
// Voor handmatige proeven vanuit de browser-tools.
(window as unknown as { __viewers3d: Viewer[] }).__viewers3d = viewers;
